import styled from "styled-components";
import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { baseUrl } from "../config";
import { ListContainer } from "./styles";
import { MaterialReactTable } from "material-react-table";
import { summarizeTimelineFilters } from "../utils/timelineFilterSummary";

const TIMELINES_COLUMNS_STORAGE_KEY = "chronofield.columns.all-frises";
const DEFAULT_TIMELINES_COLUMN_VISIBILITY = {};

const loadTimelineColumnVisibility = () => {
  try {
    const raw = localStorage.getItem(TIMELINES_COLUMNS_STORAGE_KEY);
    if (!raw) return DEFAULT_TIMELINES_COLUMN_VISIBILITY;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return DEFAULT_TIMELINES_COLUMN_VISIBILITY;
    }
    return parsed;
  } catch {
    return DEFAULT_TIMELINES_COLUMN_VISIBILITY;
  }
};

const AllTimelinesList = () => {
  const navigate = useNavigate();
  const [timelines, setTimelines] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize: 20 });
  const [columnFilters, setColumnFilters] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [categoryMap, setCategoryMap] = useState({});
  const [columnVisibility, setColumnVisibility] = useState(loadTimelineColumnVisibility);

  useEffect(() => {
    localStorage.setItem(TIMELINES_COLUMNS_STORAGE_KEY, JSON.stringify(columnVisibility));
  }, [columnVisibility]);

  useEffect(() => {
    axios
      .get(baseUrl + "/event-categories")
      .then((res) => {
        const next = {};
        (res.data || []).forEach((cat) => {
          next[String(cat.id)] = cat.name;
        });
        setCategoryMap(next);
      })
      .catch(() => {});
  }, []);

  const fetchTimelines = async (pageIndex, pageSize, columnFilters) => {
    try {
      const params = {
        page: pageIndex + 1,
        pageSize,
      };
      if (columnFilters && columnFilters.length) {
        columnFilters.forEach((f) => {
          params[f.id] = f.value;
        });
      }
      const res = await axios.get(baseUrl + "/timelines", {
        withCredentials: true,
        params,
      });
      setTimelines(res.data.data || []);
      setRowCount(res.data.total || 0);
    } catch (err) {
      console.error("Error fetching timelines:", err);
    }
  };

  useEffect(() => {
    fetchTimelines(pagination.pageIndex, pagination.pageSize, columnFilters);
  }, [pagination, columnFilters]);

  const columns = [
    {
      accessorKey: "name",
      header: "Nom",
      size: 300,
      grow: 1,
      filterVariant: "text",
    },
    {
      accessorKey: "description",
      header: "Description",
      size: 400,
      grow: 1,
      filterVariant: "text",
    },
    {
      accessorKey: "authorPseudo",
      header: "Auteur",
      size: 150,
      filterVariant: "text",
    },
    {
      accessorKey: "filters",
      header: "Filtrage",
      size: 450,
      enableColumnFilter: false,
      enableSorting: false,
      Cell: ({ row }) => (
        <div
          style={{
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: 450,
          }}
          title={summarizeTimelineFilters(row.original.filters, categoryMap, 500)}
        >
          {summarizeTimelineFilters(row.original.filters, categoryMap, 120)}
        </div>
      ),
    },
  ];

  return (
    <Container>
      <Title>Frises</Title>
      <TableWrapper>
        <MaterialReactTable
          columns={columns}
          data={timelines}
          muiTableBodyRowProps={({ row }) => ({
            onClick: () => navigate(`/timelines/${row.original.id}`),
            sx: {
              cursor: "pointer",
              backgroundColor: "var(--bg)",
              "&:hover": { backgroundColor: "var(--bg)" },
            },
          })}
          enableColumnOrdering
          enableColumnResizing
          enableColumnActions={false}
          enableHiding={false}
          enableSorting
          manualPagination
          manualFiltering
          rowCount={rowCount}
          state={{ pagination, columnFilters, columnVisibility }}
          onPaginationChange={setPagination}
          onColumnFiltersChange={setColumnFilters}
          onColumnVisibilityChange={setColumnVisibility}
          initialState={{ density: "compact" }}
          muiTopToolbarProps={{
            sx: {
              backgroundColor: "var(--bg)",
              color: "var(--text-muted)",
              "& .MuiButtonBase-root": { color: "inherit" },
            },
          }}
          muiBottomToolbarProps={{
            sx: {
              backgroundColor: "var(--bg)",
              color: "var(--text-muted)",
              "& .MuiButtonBase-root": { color: "inherit" },
            },
          }}
          muiTableBodyCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadCellProps={{
            sx: { verticalAlign: "middle", backgroundColor: "var(--bg)" },
          }}
          muiTableHeadRowProps={{
            sx: { backgroundColor: "var(--bg)" },
          }}
          muiTablePaperProps={{
            elevation: 0,
            sx: { width: "100%", maxWidth: "100%", backgroundColor: "var(--bg)" },
          }}
          muiTableContainerProps={{
            sx: { width: "100%", backgroundColor: "var(--bg)" },
          }}
        />
      </TableWrapper>
    </Container>
  );
};

export default AllTimelinesList;

const Title = styled.h1`
  margin-bottom: var(--space-4);
`;

const Container = styled(ListContainer)`
  width: 100vw;
  max-width: 100vw;
  margin: 0 auto;
  overflow-x: auto;
  box-sizing: border-box;
  padding: 0 10px;
  padding-top: var(--space-10);
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  box-sizing: border-box;
`;
